<template>
  <div>
    <table class="table table-borderless table-sm justify-content-end"
      :class="diff ? 'diff-fixed-layout' : 'table-hover'">
      <!-- In diff mode the metric header is hoisted to a single sticky header in
           FileColumns.vue (grouping is not meaningful here), so no per-group
           thead is rendered. -->
      <thead v-if="!diff">
        <tr>
          <th class="text-end text-nowrap">
            Column Name
          </th>
          <th class="count-col">
            Count
          </th>
          <th class="pct-col">
            %
          </th>
          <th class="text-end uniques-col">
            Uniques
          </th>
          <th class="text-end min-col">
            Min.
          </th>
          <th class="text-end max-col">
            Max.
          </th>
          <th class="text-end avg-col">
            Avg.
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="column in group.columns"
          :key="column.name"
        >
          <tr
            v-if="!column.isHidden"
            @click.left.stop="columnClicked('left', column)"
            @click.middle.stop="columnClicked('middle', column)"
          >
            <td :class="[columnEntryClasses(column.supplier), {
              'diff-name-col': diff,
              'text-danger': diff && currentDelta(column) < 0,
              'text-success': diff && currentDelta(column) > 0,
            }]"
              :data-test="`column-statistics_${column.name}`"
              :title="column.title" :style="diff ? null : 'max-width: 300px'">
              <template v-if="diff">
                {{ column.title }}
                <b-badge v-if="column.onlyInA" variant="warning" class="ms-1"
                  :data-test="`diff-missing-in-b_${column.name}`">
                  missing in B
                </b-badge>
                <b-badge v-if="column.onlyInB" variant="warning" class="ms-1"
                  :data-test="`diff-missing-in-a_${column.name}`">
                  missing in A
                </b-badge>
              </template>
              <template v-else>{{ column.title }}</template>
            </td>
            <template v-if="diff">
              <!-- A side without the metric's statistics (real reports leave
                   numFills/fillRate/numUniques null for some columns) gets a
                   placeholder instead of a NaN bar. -->
              <td class="count-col text-nowrap" :class="metricAlignClass" :data-test="`diff-a_${column.name}`">
                <span v-if="!hasMetricStats(column)" class="text-muted">n/a</span>
                <ColumnFillBar v-else-if="diffMetric === 'count'" :column="column" :total-rows="totalRows"
                  :variant="barVariant(column, true)" />
                <ColumnFillBar v-else-if="diffMetric === 'coverage'" :column="column" :total-rows="totalRows"
                  :variant="barVariant(column, true)" :label="coverageLabel(column)" />
                <template v-else>{{ column.numUniques }}</template>
              </td>
              <td class="count-col text-nowrap" :class="metricAlignClass" :data-test="`diff-b_${column.name}`">
                <span v-if="!hasMetricStats(column.b)" class="text-muted">n/a</span>
                <ColumnFillBar v-else-if="diffMetric === 'count'" :column="column.b" :total-rows="totalRowsB"
                  :variant="barVariant(column, false)" />
                <ColumnFillBar v-else-if="diffMetric === 'coverage'" :column="column.b" :total-rows="totalRowsB"
                  :variant="barVariant(column, false)" :label="coverageLabel(column.b)" />
                <template v-else>{{ column.b.numUniques }}</template>
              </td>
              <td class="text-end text-nowrap fw-bold delta-col" :data-test="`diff-delta_${column.name}`">
                <span :class="deltaClasses(currentDelta(column))">{{ formatDelta(currentDelta(column)) }}</span>
                <!-- R3b: surface a relative-coverage indicator only when the
                     absolute count moves opposite to the coverage (note 34360).
                     Coloured by the coverage direction, independent of the
                     absolute delta's colour. -->
                <span v-if="diffMetric === 'count' && coverageDivergent(column)"
                  class="d-block fw-normal" :class="coverageIndicatorClass(column)"
                  :title="coverageIndicatorTitle(column)"
                  :data-test="`diff-rel_${column.name}`">
                  {{ coverageIndicator(column) }}
                </span>
              </td>
            </template>
            <template v-else>
              <td>
                <ColumnFillBar :column="column" :total-rows="totalRows" />
              </td>
              <td>
                <span :id="`distribution-fill-rate-${column.name}`">
                  {{ Number(Number(column.fillRate).toFixed(2)) }}
                </span>
                <b-popover
                  v-if="'trueValue' in column && 'falseValue' in column"
                  :target="`distribution-fill-rate-${column.name}`"
                  hover
                  placement="top"
                >
                  <div>
                    <span class="px-2 me-2 bg-success" />
                    {{ column.trueValue }}:
                    {{ column.numFills }}
                    ({{ column.fillRate }}%)
                  </div>
                  <div>
                    <span class="px-2 me-2 bg-secondary" />
                    {{ column.falseValue }}:
                    {{ intComma(leftFillValues(column.numFills)) }}
                    ({{ leftFillRate(column.fillRate) }}%)
                  </div>
                </b-popover>
              </td>
              <td class="text-end text-nowrap">
                {{ column.numUniques }}
              </td>
              <template v-if="column.minLen > 0">
                <td class="text-end text-nowrap">{{ column.minLen }}</td>
              </template>
              <template v-else>
                <!-- BS4's standalone `alert-danger` painted the cell background;
                     in BS5 it only defines CSS variables (background is applied
                     by the `.alert` base class), so use the table cell variant
                     `table-danger` instead. -->
                <th class="text-end text-nowrap table-danger text-danger">
                  {{ column.minLen }}
                </th>
              </template>
              <td class="text-end text-nowrap">
                {{ column.maxLen }}
              </td>
              <td class="text-end text-nowrap">
                {{ Number(column.avgLen).toFixed(2) }}
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
    <div class="mt-auto" />
  </div>
</template>

<script>
import Humanize from "humanize-plus";
import ColumnFillBar from "./ColumnFillBar.vue";
import { parseStat } from "../../modules/reportStats.js";

export default {
  components: { ColumnFillBar },
  props: {
    group: { type: Object, default: null },
    totalRows: { type: Number, default: null },
    diff: { type: Boolean, default: false },
    diffMetric: { type: String, default: "count" },
    totalRowsB: { type: Number, default: null },
  },
  computed: {
    // Count / Coverage cells hold a right-aligned fill bar; the plain Uniques
    // numbers are left-aligned (R2c) so they sit under the header's metric label.
    metricAlignClass() {
      return this.diffMetric === "uniques" ? "text-start" : "text-end";
    },
  },
  emits: ['columnClicked'],
  data: () => ({
    booleanPairs: [
      ["yes", "no"],
      ["y", "n"],
      ["true", "false"],
      ["t", "f"],
      ["+", "-"],
      [1, 0],
      ["1", "0"],
    ],
  }),
  methods: {
    intComma: Humanize.intComma,
    columnEntryClasses(supplier) {
      return {
        // The supplier colour (profound = red) would be read as a decrease
        // next to the red/green delta colouring, so diff mode drops it and
        // keeps only the bold weight.
        [`row-${supplier}`]: !this.diff,
        "fw-bold": this.diff,
        // In diff mode the name column is a percentage width and may also carry
        // a "missing in A/B" badge, so let it wrap instead of truncating (which
        // would clip the badge). The single-report table keeps truncation.
        "text-nowrap": !this.diff,
        "text-end": true,
        "text-truncate": !this.diff,
      };
    },
    leftFillRate: (value) => 100 - parseInt(value),
    leftFillValues(value) {
      const leftValues = value.replaceAll(",", "");
      return this.totalRows - parseInt(leftValues);
    },
    columnClicked(clickType, column) {
      if (this.diff) {
        return;
      }
      this.$emit("columnClicked", clickType, column, this.group);
    },
    showNonZeroRate(column) {
      return column.type === "int-non-zero" && column.numZero !== null;
    },
    currentDelta(column) {
      if (this.diffMetric === "coverage") {
        return column.coverageDelta;
      }
      return this.diffMetric === "count"
        ? column.countDelta
        : column.uniquesDelta;
    },
    // Whether one side of the diff has the statistics the current metric
    // needs; a side without them renders a placeholder and no delta.
    hasMetricStats(side) {
      if (this.diffMetric === "uniques") {
        return parseStat(side.numUniques) != null;
      }
      return parseStat(side.numFills) != null && parseStat(side.fillRate) != null;
    },
    formatRate(value) {
      const rate = parseStat(value);
      return rate == null ? "n/a" : `${Number(rate.toFixed(1))}%`;
    },
    // Percentage label shown inside the fill bar in the Relative Coverage metric.
    coverageLabel(column) {
      return this.formatRate(column.fillRate);
    },
    // A column "diverges" when its absolute count moved one way while its
    // coverage of the (differently sized) file moved the other way.
    coverageDivergent(column) {
      const countDelta = column.countDelta;
      const coverageDelta = column.coverageDelta;
      return (
        countDelta != null &&
        coverageDelta != null &&
        countDelta !== 0 &&
        coverageDelta !== 0 &&
        Math.sign(countDelta) !== Math.sign(coverageDelta)
      );
    },
    // Coverage deltas are differences of fill rates, so they are percentage
    // points ("pp"), not a relative change in percent.
    formatCoverageDelta(delta) {
      const value = Number(delta.toFixed(1));
      return `${value > 0 ? "+" : ""}${value} pp`;
    },
    coverageIndicator(column) {
      return `(${this.formatCoverageDelta(column.coverageDelta)})`;
    },
    coverageIndicatorTitle(column) {
      const rate = (c) => this.formatRate(c.fillRate);
      return `Coverage ${rate(column)} → ${rate(column.b)} (percentage points)`;
    },
    coverageIndicatorClass(column) {
      return {
        "text-success": column.coverageDelta > 0,
        "text-danger": column.coverageDelta < 0,
      };
    },
    // Green for the file with more data in this column, red for the one with
    // less, default colour on a tie.
    barVariant(column, isFileA) {
      const delta = this.currentDelta(column);
      if (!delta) {
        return null;
      }
      const fileALarger = delta < 0;
      return (isFileA ? fileALarger : !fileALarger) ? "success" : "danger";
    },
    formatDelta(delta) {
      if (delta == null) {
        return "n/a";
      }
      if (this.diffMetric === "coverage") {
        return this.formatCoverageDelta(delta);
      }
      if (delta > 0) {
        return `+${Humanize.intComma(delta)}`;
      }
      return Humanize.intComma(delta);
    },
    deltaClasses(delta) {
      return {
        "text-success": delta > 0,
        "text-danger": delta < 0,
        "text-muted": !delta,
      };
    },
  },
};
</script>

<style scoped>
th {
  font-weight: 900;
}

td,
th {
  padding-top: 0;
  padding-bottom: 0;
}

.count-col {
  min-width: 70px;
  max-width: 70px;
}

.pct-col {
  min-width: 20px;
  max-width: 20px;
}

.uniques-col {
  min-width: 100px;
  max-width: 100px;
}

.min-col {
  min-width: 70px;
  max-width: 70px;
}

.max-col {
  min-width: 70px;
  max-width: 70px;
}

.avg-col {
  min-width: 60px;
  max-width: 60px;
}

.delta-col {
  min-width: 130px;
  max-width: 130px;
}

/* Fixed layout at full container width so every group's table sizes its
   columns identically and proportionally. Percentage widths (below) mean the
   table always fits its column exactly at any viewport — no overflow of the
   fill bars into the delta column, and the single sticky header (which mirrors
   these percentages) stays aligned with every group's rows. */
.diff-fixed-layout {
  table-layout: fixed;
  width: 100%;
}

/* Percentage column widths (sum = 100) so nothing overflows regardless of the
   container width. The delta column takes 28% so a nine-digit negative count
   ("-373,387,624", real reports have hundreds of millions of rows) fits at
   1200px without spilling into the B column. The px min/max on
   .count-col/.delta-col are for the single-report table; override them here
   for the diff table. */
.diff-fixed-layout .diff-name-col {
  width: 30%;
}
.diff-fixed-layout .count-col {
  width: 21%;
  min-width: 0;
  max-width: none;
}
.diff-fixed-layout .delta-col {
  width: 28%;
  min-width: 0;
  max-width: none;
}

/* app.scss gives every .progress a 120px minimum for the single report; in
   the fixed-layout diff table a bar wider than its cell paints over the
   delta column (it hid the minus sign of "-373,387,624" at 1200px), so let
   it shrink to the cell. */
.diff-fixed-layout :deep(.progress) {
  min-width: 0;
}

.row-customer,
.row-customer:hover {
  color: #3e4c51;
  font-weight: bold;
}

.row-profound,
.row-profound:hover {
  color: #d12239;
  font-weight: bold;
}
</style>
